import type { Relation, Article } from "~/types/article"
import { keys } from "~/utilities/injection"
import { useQuery } from "@tanstack/vue-query"

import { ContentType } from "~/models/Content/ContentType"
import { createHtmlFormatter } from "~/utilities/dom-parsing"
import type { FasitSidenote } from "~/models/FasitSidenote"

export interface FormattedSuggestedSolutionSection {
  id?: string
  relations: Relation[]
  sectionBody: string
  sectionImage?: {
    src: string
    alt?: string
  }
}

export function useMathTaskSolutions(fallbackArticle?: Ref<Article>) {
  const article = inject(keys.currentArticle, fallbackArticle)

  const { $parseStringToDocument } = useNuxtApp()

  const getContent = useGetContent()

  return useQuery({
    enabled: computed(
      () =>
        !!article?.value.locationId &&
        !!article.value.mathTask &&
        ["UMathTaskWithImageArticle", "UMathTaskArticle"].includes(article.value.template),
    ),
    queryKey: ["ibexa", "math-task-solutions", computed(() => article?.value.locationId)] as const,
    queryFn({ queryKey }) {
      const [, , locationId] = queryKey

      if (typeof locationId !== "number") {
        throw new Error("Invalid locationId provided")
      }

      return getContent({
        childDepth: 2,
        contentType: [ContentType.MathTaskIbexa],
        parentLocationId: [locationId],
        sortField: "priority",
        sortOrder: "asc",
      })
    },
    select(mathTasks) {
      if (!mathTasks?.length) {
        return null
      }

      const suggestedSolutions = new Map<number, FormattedSuggestedSolutionSection[]>()

      const solutions: string[] = []
      const sidenotes: (Relation | FasitSidenote)[] = []

      for (const task of mathTasks) {
        const solution = task.children.find(isContentType(ContentType.SolutionIbexa))
        if (solution) {
          solutions.push($parseStringToDocument(solution.solutionText.html, ["mathml"]).body.innerHTML)
        }

        const sections = task
          .children
          .find(isContentType(ContentType.SuggestedSolutionIbexa))
          ?.children
          ?.filter(isContentType(ContentType.SuggestedSolutionSectionIbexa))
          .sort(byPriority)
          .map(section => {
            const relations = (section.relatedItems as Relation[]) ?? []
            const formatHtml = createHtmlFormatter(relations, $parseStringToDocument)
            return {
              id: section.locationId?.toString(),
              relations,
              sectionBody: formatHtml(section.sectionBody.html),
              sectionImage: section.sectionImage?.src
                ? {
                    src: section.sectionImage.src,
                    alt: section.sectionImage.alt,
                  }
                : undefined,
            }
          })

        if (sections?.length) {
          suggestedSolutions.set(task.locationId, sections)
          sidenotes.push(...sections.flatMap(s => s.relations))
        }
      }

      if (solutions.length) {
        sidenotes.unshift({
          identifier: "solution",
          title: "Fasit",
          locationId: "fasit",
          solutionText: solutions.join("<br>"),
        })
      }

      return {
        suggestedSolutions,
        sidenotes,
      }
    },
  })
}
